// components/AuthPage/SignupForm.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { registerUser, verifyEmail, resendCode } from "../../APIs/authApi";
import FloatingInput from "./FloatingInput";
import { AuthButton } from "./LoginForm";

/* ── Password strength ──────────────────────────── */
const LEVELS = [
  { label: "Too short", color: "#ef4444" },
  { label: "Weak",      color: "#f97316" },
  { label: "Fair",      color: "#eab308" },
  { label: "Good",      color: "#4ade80" },
  { label: "Strong",    color: "#22d3ee" },
];

function getStrength(pw) {
  if (!pw || pw.length < 4) return 0;
  let s = 1;
  if (pw.length >= 8)           s++;
  if (/[A-Z]/.test(pw))         s++;
  if (/[0-9]/.test(pw))         s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 4);
}

/* ── Match icons ────────────────────────────────── */
const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const CrossIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

/* ── OTP Input Screen ───────────────────────────── */
const RESEND_SECONDS = 60;

function OtpScreen({ email, onVerified }) {
  const [digits,   setDigits]   = useState(["", "", "", "", "", ""]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [success,  setSuccess]  = useState(false);
  const [timer,    setTimer]    = useState(RESEND_SECONDS);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef([]);

  // Countdown timer
  useEffect(() => {
    if (timer <= 0) return;
    const id = setTimeout(() => setTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timer]);

  const handleDigitChange = (index, value) => {
    // Accept only digits; handle paste of full code
    if (value.length > 1) {
      const pasted = value.replace(/\D/g, "").slice(0, 6);
      const next = ["", "", "", "", "", ""];
      for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
      setDigits(next);
      const focusAt = Math.min(pasted.length, 5);
      inputRefs.current[focusAt]?.focus();
      return;
    }
    const digit = value.replace(/\D/g, "");
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = useCallback(async () => {
    const code = digits.join("");
    if (code.length < 6) { setError("Enter all 6 digits."); return; }
    setLoading(true); setError(null);
    try {
      await verifyEmail(email, code);
      setSuccess(true);
      setTimeout(() => onVerified(), 1200);
    } catch (err) {
      const d = err.response?.data;
      setError(d?.error || "Verification failed. Please try again.");
      setDigits(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }, [digits, email, onVerified]);

  // Auto-submit when all 6 digits are filled
  useEffect(() => {
    if (digits.every((d) => d !== "") && !loading && !success) {
      handleVerify();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits]);

  const handleResend = async () => {
    if (timer > 0) return;
    setResending(true); setError(null);
    try {
      await resendCode(email);
      setTimer(RESEND_SECONDS);
      setDigits(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } catch (err) {
      const d = err.response?.data;
      setError(d?.error || "Could not resend code. Try again.");
    } finally {
      setResending(false); }
  };

  return (
    <div className="animate-[fin_.3s_cubic-bezier(.16,1,.3,1)_both]">

      {/* Header */}
      <div className="mb-6 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-3"
             style={{ background: "rgba(79,142,247,0.12)", border: "1px solid rgba(79,142,247,0.2)" }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4f8ef7" strokeWidth="1.8">
            <rect x="2" y="4" width="20" height="16" rx="2"/>
            <path d="M22 7l-10 7L2 7"/>
          </svg>
        </div>
        <p className="text-[13px] text-slate-400 leading-relaxed">
          We sent a 6-digit code to<br/>
          <span className="text-slate-200 font-medium">{email}</span>
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 rounded-[9px] border border-red-500/20 bg-red-500/[.07] px-3 py-2.5 text-[12.5px] text-red-300/90 leading-[1.5]">
          {error}
        </div>
      )}

      {/* Success */}
      {success && (
        <div className="mb-4 rounded-[9px] border border-green-500/20 bg-green-500/[.07] px-3 py-2.5 text-[12.5px] text-green-300/90 leading-[1.5] text-center">
          Email verified! Redirecting to sign in…
        </div>
      )}

      {/* 6 digit boxes */}
      <div className="flex gap-1.5 sm:gap-2.5 justify-center mb-6">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={d}
            onChange={(e) => handleDigitChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            disabled={loading || success}
            className={[
              "w-9 h-11 sm:w-11 sm:h-14 text-center text-[18px] sm:text-[22px] font-bold rounded-xl outline-none transition-all duration-150",
              "bg-white/[0.04] border text-white caret-blue-400",
              d
                ? "border-blue-500/50 bg-blue-500/[0.07]"
                : "border-white/[0.08] focus:border-blue-500/40 focus:bg-white/[0.06]",
              (loading || success) ? "opacity-50 cursor-not-allowed" : "",
            ].join(" ")}
          />
        ))}
      </div>

      {/* Verify button */}
      <button
        onClick={handleVerify}
        disabled={loading || success || digits.join("").length < 6}
        className={[
          "w-full py-[13px] rounded-xl text-[13.5px] font-semibold tracking-[.04em] transition-all duration-200",
          loading || success || digits.join("").length < 6
            ? "bg-blue-600/30 text-blue-400/50 cursor-not-allowed"
            : "bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_20px_rgba(37,99,235,0.35)]",
        ].join(" ")}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity=".25"/>
              <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            </svg>
            Verifying…
          </span>
        ) : "Verify Email"}
      </button>

      {/* Resend */}
      <p className="mt-4 text-center text-[12px] text-slate-600">
        Didn't receive it?{" "}
        {timer > 0 ? (
          <span className="text-slate-500">Resend in {timer}s</span>
        ) : (
          <button
            onClick={handleResend}
            disabled={resending}
            className="text-blue-500 hover:text-blue-400 transition-colors disabled:opacity-50"
          >
            {resending ? "Sending…" : "Resend code"}
          </button>
        )}
      </p>
    </div>
  );
}

/* ── Main Signup Form ───────────────────────────── */
export default function SignupForm({ onSuccess, pendingEmail, onVerified }) {
  const [form, setForm]   = useState({ email: "", username: "", password: "", password2: "" });
  const [loading, setLoad] = useState(false);
  const [error, setError]  = useState(null);

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const score   = getStrength(form.password);
  const { label: strLabel, color: strColor } = LEVELS[score];
  const matched  = form.password2.length > 0 && form.password === form.password2;
  const mismatch = form.password2.length > 0 && form.password !== form.password2;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password2) { setError("Passwords do not match."); return; }
    setLoad(true); setError(null);
    try {
      const data = await registerUser(form);
      onSuccess?.(data);
    } catch (err) {
      const d = err.response?.data;
      setError(typeof d === "string" ? d : d ? Object.values(d).flat().join(" ") : "Registration failed.");
    } finally { setLoad(false); }
  };

  // If parent passes a pendingEmail, show OTP screen
  if (pendingEmail) {
    return <OtpScreen email={pendingEmail} onVerified={onVerified} />;
  }

  return (
    <form onSubmit={onSubmit} className="animate-[fin_.3s_cubic-bezier(.16,1,.3,1)_both]">

      {error && (
        <div className="mb-3 rounded-[9px] border border-red-500/20 bg-red-500/[.07] px-3 py-2.5 text-[12.5px] text-red-300/90 leading-[1.5]">
          {error}
        </div>
      )}

      <FloatingInput name="email"    label="Email address" type="email"
        value={form.email}    onChange={onChange} required autoComplete="email"    className="mb-3.5" />

      <FloatingInput name="username" label="Username"      type="text"
        value={form.username} onChange={onChange} required autoComplete="username" className="mb-3.5" />

      {/* Password + strength */}
      <div className="mb-1">
        <FloatingInput
          name="password" label="Password" type="password"
          value={form.password} onChange={onChange}
          required autoComplete="new-password"
        />
        {form.password && (
          <div className="mt-1.5">
            <div className="flex gap-1">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex-1 h-[2px] rounded-sm transition-colors duration-300"
                  style={{ background: i < score ? strColor : "rgba(255,255,255,0.06)" }}
                />
              ))}
            </div>
            <p className="text-right mt-1 text-[10px] tracking-widest uppercase transition-colors"
               style={{ color: strColor }}>
              {strLabel}
            </p>
          </div>
        )}
      </div>

      {/* Confirm password */}
      <FloatingInput
        name="password2" label="Confirm password" type="password"
        value={form.password2} onChange={onChange}
        required autoComplete="new-password"
        suffixIcon={matched ? <CheckIcon /> : mismatch ? <CrossIcon /> : null}
        className="mb-3.5 mt-2"
      />

      <AuthButton loading={loading} label="Create Account" loadingLabel="Creating account…" />
    </form>
  );
}
